/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { useCallback } from 'react'
import { css, jsx } from '@emotion/react'
import { useNavigate } from 'react-router-dom'

import { EuiButton, EuiButtonEmpty, EuiFlexGroup, EuiText, EuiTourStep } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

const FeedbackNotification = () => {
  const navigate = useNavigate()
  const [_, flags] = useFlagsWhenLoaded()
  const [showFeedbackNotification, setShowFeedbackNotification] = useLocalStorage(
    LocalStorageKey.billingFeedbackAvailable,
    true,
  )

  const isFeedbackNotificationEnabled = flags.billingFeedback && showFeedbackNotification

  const handleDismiss = useCallback(() => {
    setShowFeedbackNotification(false)
  }, [setShowFeedbackNotification])

  const handleConfirm = useCallback(async () => {
    // we want to set local storage before redirect and component unmount, thats why we need await here
    await setShowFeedbackNotification(false)
    navigate('../feedback')
  }, [navigate, setShowFeedbackNotification])

  return (
    // we want to make sure that feedback notification will not cover drift chat that is displayed in the same corner
    <div css={css({ position: 'fixed', bottom: 230, right: 24 })}>
      <EuiTourStep
        content={
          <EuiText size='m' color='subdued' data-test-id='billing-feedback.survey.content'>
            <FormattedMessage
              id='billing-feedback.survey.text'
              defaultMessage={`We'd love your feedback to make your experience better. It will only take a few seconds to complete.`}
            />
          </EuiText>
        }
        title={
          <FormattedMessage
            id='billing-feedback.survey.title'
            defaultMessage='Help us improve the Elastic billing platform'
          />
        }
        step={1}
        stepsTotal={1}
        footerAction={
          <EuiFlexGroup alignItems='center'>
            <EuiButtonEmpty
              data-test-id='billing-feedback.survey.dismiss'
              size='s'
              color='text'
              onClick={handleDismiss}
            >
              <FormattedMessage id='billing-feedback.survey.dismiss' defaultMessage='No, thanks' />
            </EuiButtonEmpty>
            <EuiButton data-test-id='billing-feedback.survey.confirm' onClick={handleConfirm}>
              <FormattedMessage
                id='billing-feedback.survey.confirm'
                defaultMessage='Yes, take me there'
              />
            </EuiButton>
          </EuiFlexGroup>
        }
        onFinish={handleDismiss}
        isStepOpen={isFeedbackNotificationEnabled}
        anchorPosition='rightCenter'
        zIndex={3}
        maxWidth={380}
        arrowChildren={false}
        hasArrow={false}
        repositionOnScroll={true}
      >
        {/* // we need this empty span to be able to attach the tour component into element in DOM */}
        <span />
      </EuiTourStep>
    </div>
  )
}

export default FeedbackNotification
